/**
 * FilterBar - Visual filter builder component
 */

import { useState, useMemo, useRef, useEffect } from 'react';
import type { FilterCondition as FilterConditionType, ColumnDefinition, FilterLogic } from '../columns/types';
import { FilterCondition } from './FilterCondition';

interface FilterBarProps {
  columns: ColumnDefinition[];
  filters: FilterConditionType[];
  filterLogic: FilterLogic;
  siteId?: string;
  onFiltersChange: (filters: FilterConditionType[]) => void;
  onFilterLogicChange: (logic: FilterLogic) => void;
  onAddFilter: (column: ColumnDefinition) => void;
  onUpdateFilter: (id: string, updates: Partial<Omit<FilterConditionType, 'id'>>) => void;
  onRemoveFilter: (id: string) => void;
  onClearFilters: () => void;
}

export function FilterBar({
  columns,
  filters,
  filterLogic,
  siteId,
  onFiltersChange: _onFiltersChange,
  onFilterLogicChange,
  onAddFilter,
  onUpdateFilter,
  onRemoveFilter,
  onClearFilters,
}: FilterBarProps) {
  const [isAddMenuOpen, setIsAddMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  // Filterable columns only
  const filterableColumns = useMemo(
    () => columns.filter(c => c.filterable),
    [columns]
  );

  // Close menu when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsAddMenuOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle adding a filter
  const handleAddFilter = (column: ColumnDefinition) => {
    onAddFilter(column);
    setIsAddMenuOpen(false);
  };

  return (
    <div className="space-y-2">
      {/* Filter controls row */}
      <div className="flex items-center gap-2">
        {/* Add Filter button with dropdown */}
        <div className="relative">
          <button
            ref={buttonRef}
            onClick={() => setIsAddMenuOpen(!isAddMenuOpen)}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-zinc-200 bg-zinc-800 hover:bg-zinc-700 border border-zinc-600 rounded-lg transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Add Filter
          </button>

          {/* Column selection dropdown */}
          {isAddMenuOpen && (
            <div
              ref={menuRef}
              className="absolute top-full left-0 mt-1 w-56 bg-zinc-800 border border-zinc-700 rounded-lg shadow-xl z-50 py-1 max-h-64 overflow-y-auto"
            >
              {filterableColumns.length === 0 ? (
                <div className="px-3 py-2 text-sm text-zinc-500">
                  No filterable columns
                </div>
              ) : (
                filterableColumns.map(column => (
                  <button
                    key={column.key}
                    onClick={() => handleAddFilter(column)}
                    className="w-full text-left px-3 py-2 text-sm text-zinc-200 hover:bg-zinc-700 transition-colors flex items-center gap-2"
                  >
                    <ColumnTypeIcon type={column.type} />
                    {column.label}
                  </button>
                ))
              )}
            </div>
          )}
        </div>

        {/* Filter logic toggle (only show when 2+ filters) */}
        {filters.length >= 2 && (
          <div className="flex items-center gap-1 px-2 py-1 bg-zinc-800/50 rounded-lg border border-zinc-700">
            <button
              onClick={() => onFilterLogicChange('and')}
              className={`px-2 py-0.5 text-xs font-medium rounded transition-colors ${
                filterLogic === 'and'
                  ? 'bg-blue-600 text-white'
                  : 'text-zinc-400 hover:text-zinc-200'
              }`}
            >
              AND
            </button>
            <button
              onClick={() => onFilterLogicChange('or')}
              className={`px-2 py-0.5 text-xs font-medium rounded transition-colors ${
                filterLogic === 'or'
                  ? 'bg-blue-600 text-white'
                  : 'text-zinc-400 hover:text-zinc-200'
              }`}
            >
              OR
            </button>
          </div>
        )}

        {/* Clear all button */}
        {filters.length > 0 && (
          <button
            onClick={onClearFilters}
            className="px-2 py-1.5 text-sm text-zinc-400 hover:text-zinc-200 transition-colors"
          >
            Clear all
          </button>
        )}

        {/* Active filter count badge */}
        {filters.length > 0 && (
          <span className="px-2 py-0.5 text-xs font-medium bg-blue-600/20 text-blue-400 rounded-full">
            {filters.length} filter{filters.length !== 1 ? 's' : ''} active
          </span>
        )}
      </div>

      {/* Active filters */}
      {filters.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {filters.map((filter, index) => (
            <div key={filter.id} className="flex items-center gap-1">
              {/* Show logic connector between filters */}
              {index > 0 && (
                <span className="text-xs text-zinc-500 px-1">
                  {filterLogic.toUpperCase()}
                </span>
              )}
              <FilterCondition
                filter={filter}
                columns={columns}
                siteId={siteId}
                onUpdate={onUpdateFilter}
                onRemove={onRemoveFilter}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Small icon to show column type
function ColumnTypeIcon({ type }: { type: string }) {
  const iconClass = "w-4 h-4 text-zinc-500";

  switch (type) {
    case 'text':
      return (
        <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h7" />
        </svg>
      );
    case 'number':
    case 'currency':
      return (
        <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14" />
        </svg>
      );
    case 'date':
      return (
        <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      );
    case 'boolean':
      return (
        <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
        </svg>
      );
    case 'select':
    case 'multiselect':
      return (
        <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
        </svg>
      );
    case 'image':
      return (
        <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      );
    case 'url':
      return (
        <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
        </svg>
      );
    default:
      return (
        <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16m-7 6h7" />
        </svg>
      );
  }
}
